import type { FlagManifest } from "./types";

/**
 * Henlo feature flag manifest.
 *
 * Hibernation defaults: every flag here is `default: true` (disabled). The
 * project is in graceful sunset; flags flip back to `false` only when their
 * `restoreWhen` condition is met.
 *
 * Conventions:
 * - Flag name is SCREAMING_SNAKE_CASE, matches `FLAG_<NAME>` env var suffix.
 * - Hibernation flags are grouped by surface in declaration order.
 * - When a backend has both reads and writes, declare them as a related pair.
 *
 * To override at runtime without a deploy: set `FLAG_<NAME>=false` (or `0`)
 * in Vercel env settings. The reader (`./reader.ts`) honors per-environment
 * overrides over the manifest default.
 */
export const FLAGS = {
  // ─────────────────────────────────────────────────────────────────────────
  // Lobby · Tetris cabinet (shipped session 1)
  // ─────────────────────────────────────────────────────────────────────────
  TETRIS_WRITES_DISABLED: {
    default: true,
    description: "Tetris score saves and brick increments via Supabase.",
    affects: ["apps/henlo-interface/actions/tetris.ts"],
    surface: "lobby",
    userImpact: "invisible",
    restoreWhen: "never — graceful sunset, no return planned",
    related: ["TETRIS_READS_DISABLED"],
  },
  TETRIS_READS_DISABLED: {
    default: true,
    description: "Tetris leaderboard + active event reads via Supabase.",
    affects: [
      "apps/henlo-interface/app/api/tetris/leaderboard/route.ts",
      "apps/henlo-interface/app/api/tetris/events/active/route.ts",
      "apps/henlo-interface/app/api/tetris/events/[slug]/leaderboard/route.ts",
    ],
    surface: "lobby",
    userImpact: "disabled-with-sign",
    restoreWhen: "never — graceful sunset, no return planned",
    related: ["TETRIS_WRITES_DISABLED"],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Lobby chrome · Live presence
  // ─────────────────────────────────────────────────────────────────────────
  PRESENCE_DISABLED: {
    default: true,
    description: "Live-presence reads and writes via Supabase Realtime.",
    affects: [
      "apps/henlo-interface/components/live-presence.tsx",
      "apps/henlo-interface/hooks/presence/use-presence.ts",
      "apps/henlo-interface/app/api/presence/summary/route.ts",
      "apps/henlo-interface/actions/presence.ts",
    ],
    surface: "lobby",
    userImpact: "degraded",
    restoreWhen: "never — graceful sunset, no return planned",
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Profile (Supabase identity layer)
  // ─────────────────────────────────────────────────────────────────────────
  PROFILE_READS_DISABLED: {
    default: true,
    description: "Profile lookup, settings, and username availability reads.",
    affects: [
      "apps/henlo-interface/app/api/profile/lookup/route.ts",
      "apps/henlo-interface/app/api/profile/settings/route.ts",
      "apps/henlo-interface/app/api/profile/username/route.ts",
      "apps/henlo-interface/hooks/profile/use-current-profile.ts",
    ],
    surface: "profile",
    userImpact: "degraded",
    restoreWhen: "never — graceful sunset, no return planned",
    related: ["PROFILE_WRITES_DISABLED"],
  },
  PROFILE_WRITES_DISABLED: {
    default: true,
    description: "Profile creation, settings updates, username changes.",
    affects: ["apps/henlo-interface/actions/profile.ts"],
    surface: "profile",
    userImpact: "invisible",
    restoreWhen: "never — graceful sunset, no return planned",
    related: ["PROFILE_READS_DISABLED"],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Gallery (Supabase + Vercel Blob)
  // ─────────────────────────────────────────────────────────────────────────
  GALLERY_DISABLED: {
    default: true,
    description: "Gallery list, submissions, and likes via Supabase.",
    affects: [
      "apps/henlo-interface/app/(core)/gallery/page.tsx",
      "apps/henlo-interface/app/api/gallery/list/route.ts",
      "apps/henlo-interface/actions/gallery.ts",
    ],
    surface: "gallery",
    userImpact: "disabled-with-sign",
    restoreWhen: "never — graceful sunset, no return planned",
  },

  // ─────────────────────────────────────────────────────────────────────────
  // Quests (CubQuests detach — partner-facing API)
  // ─────────────────────────────────────────────────────────────────────────
  QUEST_VERIFIER_DISABLED: {
    default: true,
    description:
      "Supabase-backed quest verifiers (profile, gallery_*, tetris_achievement, presence_visits). Envio-backed verifiers (wall_deposit, token_burn) and `manual` are NOT gated by this flag — they keep working through hibernation.",
    affects: ["apps/henlo-interface/app/api/quests/verify/route.ts"],
    surface: "partner-api",
    userImpact: "invisible",
    restoreWhen: "never — graceful sunset, no return planned",
  },

  // ─────────────────────────────────────────────────────────────────────────
  // EARN row · BGT rewards discontinued (banner shipped 9b554e5)
  // ─────────────────────────────────────────────────────────────────────────
  BGT_REWARDS_DISCONTINUED: {
    default: true,
    description:
      "Berachain Foundation reward-vault realignment — Henlockers no longer earn BGT. Stats viewing, deposits, and withdraws stay onchain-operational; this flag governs the in-world visual cabinet that frames lock + vault + wall as paused for renovations.",
    affects: [
      "apps/henlo-interface/app/(core)/lock/page.tsx",
      "apps/henlo-interface/app/(core)/vault/page.tsx",
      "apps/henlo-interface/app/(core)/wall/page.tsx",
    ],
    surface: "earn-row",
    userImpact: "disabled-with-sign",
    restoreWhen: "never — Foundation reward realignment is permanent",
  },
} as const satisfies FlagManifest;

export type FlagName = keyof typeof FLAGS;
